/**
 * Utilitaire pour obtenir les coordonnées d'une commune
 * 
 * Comme il y a plus de 35 000 communes en France, nous ne pouvons pas
 * stocker toutes leurs coordonnées. À la place, nous calculons le centroïd
 * à partir des données GeoJSON lorsque disponible.
 */

/**
 * Calcule le centroïd d'un polygone ou multipolygone GeoJSON
 * @param {Object} geometry - Géométrie GeoJSON
 * @returns {[number, number]|null} Coordonnées [lat, lng] du centre
 */
export function calculateCentroid(geometry) {
    if (!geometry || !geometry.coordinates) return null;

    try {
        let allCoords = [];

        // Gérer les différents types de géométrie
        if (geometry.type === 'Polygon') {
            // Prendre seulement l'anneau extérieur (premier élément)
            allCoords = geometry.coordinates[0];
        } else if (geometry.type === 'MultiPolygon') {
            // Prendre tous les anneaux extérieurs
            geometry.coordinates.forEach(polygon => {
                allCoords = allCoords.concat(polygon[0]);
            });
        } else {
            return null;
        }

        // Calculer la moyenne des coordonnées
        if (allCoords.length === 0) return null;

        const sum = allCoords.reduce(
            (acc, coord) => {
                return [acc[0] + coord[0], acc[1] + coord[1]];
            },
            [0, 0]
        );

        const avgLng = sum[0] / allCoords.length;
        const avgLat = sum[1] / allCoords.length;

        return [avgLat, avgLng];
    } catch (error) {
        console.error('Erreur lors du calcul du centroïd:', error);
        return null;
    }
}

/**
 * Cache pour stocker les centroïds calculés
 * Évite de recalculer plusieurs fois pour la même commune
 */
const centroidCache = new Map();

/**
 * Récupère ou calcule les coordonnées d'une commune
 * @param {string} inseeCode - Code INSEE de la commune
 * @param {Object} geoJsonFeature - Feature GeoJSON de la commune (optionnel)
 * @returns {[number, number]|null} Coordonnées [lat, lng]
 */
export function getCityCoordinates(inseeCode, geoJsonFeature = null) {
    if (!inseeCode) return null;

    // Vérifier le cache
    if (centroidCache.has(inseeCode)) {
        return centroidCache.get(inseeCode);
    }

    // Si on a la feature GeoJSON, calculer le centroïd
    if (geoJsonFeature && geoJsonFeature.geometry) {
        const coords = calculateCentroid(geoJsonFeature.geometry);
        if (coords) {
            centroidCache.set(inseeCode, coords);
            return coords;
        }
    }

    return null;
}

/**
 * Extrait les coordonnées de toutes les communes d'un GeoJSON
 * @param {Object} geojson - GeoJSON contenant les communes
 * @returns {Map<string, [number, number]>} Map inseeCode => coordonnées
 */
export function extractCityCoordinatesFromGeoJSON(geojson) {
    const coordinates = new Map();

    if (!geojson || !geojson.features) return coordinates;

    geojson.features.forEach(feature => {
        const inseeCode = feature.properties?.code || feature.properties?.insee;
        if (inseeCode) {
            const coords = calculateCentroid(feature.geometry);
            if (coords) {
                coordinates.set(inseeCode, coords);
                centroidCache.set(inseeCode, coords);
            }
        }
    });

    return coordinates;
}

/**
 * Vérifie si une commune a des coordonnées disponibles
 * @param {string} inseeCode - Code INSEE de la commune
 * @returns {boolean}
 */
export function hasCityCoordinates(inseeCode) {
    return inseeCode && centroidCache.has(inseeCode);
}
